import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {linkResolver} from 'prismicio'
import type {EventDocument} from 'prismicio-types'
import {memo, useCallback, useContext} from 'react'
import {ButtonV2} from 'src/common/ButtonV2'
import Dialog from 'src/common/Dialog'
import {DialogContext} from 'src/context/DialogContext'
import {useHubspotForm} from 'src/Event/hooks/useHubspotForm'
import {Input} from '../../../RegisterForm/components/Input'
import {Select} from '../../../RegisterForm/components/Select'
import {COUNTRIES} from '../../../RegisterForm/constants/formOptions'
import {setVideoUnlockedCookie} from '../../helpers'

type Props = {
  onSubmitSuccess: () => void
  data: EventDocument['data']
  pageUid: string
  videoUrl: string
}

export const VideoEmailDialog = memo(({onSubmitSuccess, data, pageUid, videoUrl}: Props) => {
  const context = useContext(DialogContext)

  const handleSuccess = useCallback(() => {
    setVideoUnlockedCookie(videoUrl)
    onSubmitSuccess()
    context?.setIsDialogOpen(false)
  }, [onSubmitSuccess, context, videoUrl])

  const {form, submit, isSubmitSuccessful, isSubmitting} = useHubspotForm({
    formId: data.video_hubspot_form_id,
    pageUid,
    trackingLocation: 'component-video-register-form',
    onSuccess: handleSuccess,
    trackDataType: 'video-registration',
  })

  const {handleSubmit, register, control, watch} = form
  const allValues = watch()

  const handleClose = () => {
    context?.setIsDialogOpen(false)
  }

  if (!context) {
    return null
  }

  return (
    <Dialog onClose={handleClose}>
      <div className="my-auto flex w-full max-w-[324px] flex-col gap-3 rounded-lg bg-moss-white p-4 shadow-lg md:p-5">
        {isFilled.keyText(data.form_title) && (
          <div className="flex w-full flex-col gap-1">
            <h3 className="text-center text-18 font-medium leading-[23px] text-moss-black">{data.form_title}</h3>
          </div>
        )}

        <form
          className={cn('flex w-full flex-col gap-3 text-moss-black', {
            hidden: isSubmitSuccessful,
          })}
          onSubmit={handleSubmit(submit)}
        >
          {isFilled.group(data.video_access_form) &&
            data.video_access_form.map((field) => {
              if (!isFilled.keyText(field.field_name)) {
                return null
              }

              const fieldType = field.field_type || 'text'
              const fieldName = field.field_name
              const isSelect = fieldType === 'select'

              if (isSelect && fieldName === 'billing_country') {
                return (
                  <Select
                    key={fieldName}
                    placeholder={field.field_placeholder || 'Select country'}
                    control={control}
                    fieldName={fieldName}
                    requiredMessage={field.required_validation_message || 'Please select a country.'}
                    isRequired={field.is_required || false}
                    options={COUNTRIES}
                  />
                )
              }

              return (
                <Input
                  key={fieldName}
                  placeholder={field.field_placeholder || ''}
                  register={register}
                  fieldName={fieldName}
                  type={fieldType}
                  requiredMessage={field.required_validation_message || 'This field is required.'}
                  isRequired={field.is_required || false}
                  value={allValues[fieldName] as string}
                  emailValidationMessage={
                    fieldType === 'email' && isFilled.keyText(data.email_validation_text)
                      ? data.email_validation_text
                      : undefined
                  }
                />
              )
            })}

          <label className="flex w-full items-center gap-3">
            <input type="checkbox" className="size-3.5 shrink-0 cursor-pointer rounded border border-[#ccc]" required />
            <PrismicRichText
              components={{
                paragraph: ({children}) => <p className="text-tg-body-s text-moss-grey-400">{children}</p>,
                hyperlink: ({children, node}) => (
                  <Link
                    className="text-moss-black underline"
                    href={linkResolver(node.data)}
                    {...(node.data.link_type === 'Web' && node.data.target && {target: node.data.target})}
                  >
                    {children}
                  </Link>
                ),
              }}
              field={data.privacy_policy}
            />
          </label>

          <ButtonV2
            className="w-full"
            variant="primary"
            trackingData={{
              mktgId: 'form-video-register-form',
              location: `component-video-email-dialog`,
              dataType: 'submit-button',
              pageName: pageUid,
              label: data.lead_cta_button_text!,
            }}
            type="submit"
            disabled={isSubmitting}
            border={false}
          >
            {isFilled.keyText(data.button_submitting_text) && isSubmitting
              ? data.button_submitting_text
              : data.lead_cta_button_text}
          </ButtonV2>
        </form>
      </div>
    </Dialog>
  )
})

VideoEmailDialog.displayName = 'VideoEmailDialog'
